import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { setGrab, setGrabbing } from "./dragCursor";

/* セクション脇のスクロール回転ワイヤーフレームオブジェクト */

function sphereGeo(R, rings, segs) {
  const pts = [];
  const edges = [];
  for (let i = 1; i < rings; i++) {
    const phi = (Math.PI * i) / rings;
    for (let j = 0; j < segs; j++) {
      const th = (2 * Math.PI * j) / segs;
      pts.push([
        R * Math.sin(phi) * Math.cos(th),
        R * Math.cos(phi),
        R * Math.sin(phi) * Math.sin(th),
      ]);
    }
  }
  const idx = (i, j) => (i - 1) * segs + ((j + segs) % segs);
  for (let i = 1; i < rings; i++)
    for (let j = 0; j < segs; j++) {
      edges.push([idx(i, j), idx(i, j + 1)]); // 緯線
      if (i < rings - 1) edges.push([idx(i, j), idx(i + 1, j)]); // 経線
    }
  return { pts, edges };
}

function mobiusGeo(R, w, segU, segV) {
  const pts = [];
  const edges = [];
  for (let i = 0; i < segU; i++) {
    const u = (2 * Math.PI * i) / segU;
    for (let j = 0; j < segV; j++) {
      const v = -w + (2 * w * j) / (segV - 1);
      const c = R + v * Math.cos(u / 2); /* 一周でちょうど半回転ひねる */
      pts.push([c * Math.cos(u), v * Math.sin(u / 2), c * Math.sin(u)]);
    }
  }
  const idx = (i, j) => i * segV + j;
  for (let i = 0; i < segU; i++)
    for (let j = 0; j < segV; j++) {
      if (j < segV - 1) edges.push([idx(i, j), idx(i, j + 1)]);
      if (i < segU - 1) edges.push([idx(i, j), idx(i + 1, j)]);
      else edges.push([idx(i, j), idx(0, segV - 1 - j)]); // 継ぎ目: 半ひねり接続
    }
  return { pts, edges };
}

function tesseractGeo(S, n) {
  const pts = [];
  const edges = [];
  const faces = [];
  /* 16頂点: idx = x*8+y*4+z*2+w */
  for (let x = 0; x < 2; x++)
    for (let y = 0; y < 2; y++)
      for (let z = 0; z < 2; z++)
        for (let w = 0; w < 2; w++)
          pts.push([(x * 2 - 1) * S, (y * 2 - 1) * S, (z * 2 - 1) * S, (w * 2 - 1) * S]);
  /* 24面: 4軸から2軸を選んで動かし、残り2軸を固定 */
  for (let a = 0; a < 4; a++)
    for (let b = a + 1; b < 4; b++) {
      const rest = [0, 1, 2, 3].filter((k) => k !== a && k !== b);
      for (let va = 0; va < 2; va++)
        for (let vb = 0; vb < 2; vb++) {
          const corner = (ai, bi) => {
            const bits = [0, 0, 0, 0];
            bits[rest[0]] = va;
            bits[rest[1]] = vb;
            bits[a] = ai;
            bits[b] = bi;
            return bits[0] * 8 + bits[1] * 4 + bits[2] * 2 + bits[3];
          };
          faces.push([corner(0, 0), corner(0, 1), corner(1, 1), corner(1, 0)]);
        }
    }
  /* 32辺（1ビット違いの頂点対）を4D空間のまま分割してワイヤー化 */
  for (let i = 0; i < 16; i++)
    for (const bit of [8, 4, 2, 1]) {
      const j = i ^ bit;
      if (j > i) {
        const A = pts[i];
        const B = pts[j];
        let prev = i;
        for (let k = 1; k <= n; k++) {
          if (k < n) {
            pts.push(A.map((v, m) => v + ((B[m] - v) * k) / n));
            edges.push([prev, pts.length - 1]);
            prev = pts.length - 1;
          } else edges.push([prev, j]);
        }
      }
    }
  return { pts, edges, faces, dim4: true, S4: S };
}

/* 会社ロゴ「TR」: 字形のセンターラインに線幅と奥行きを持たせた角柱状ワイヤー */
function logoGeo(halfW = 20, depth = 20) {
  const T_HUE = 187; /* ティール */
  const R_HUE = 42; /* イエロー */
  const pts = [];
  const edges = [];
  const hues = [];

  /* 直線を n 分割してサンプル点列に */
  const line = (a, b, n) => {
    const out = [];
    for (let k = 0; k <= n; k++)
      out.push([a[0] + ((b[0] - a[0]) * k) / n, a[1] + ((b[1] - a[1]) * k) / n]);
    return out;
  };
  /* 2次ベジェのサンプル点列 */
  const quad = (p0, p1, p2, n) => {
    const out = [];
    for (let k = 0; k <= n; k++) {
      const t = k / n;
      const u = 1 - t;
      out.push([
        u * u * p0[0] + 2 * u * t * p1[0] + t * t * p2[0],
        u * u * p0[1] + 2 * u * t * p1[1] + t * t * p2[1],
      ]);
    }
    return out;
  };
  /* Rのボウル: 半楕円。両端は縦棒を通り抜けて左面(x=30)まで伸ばし、端面を揃える */
  const bowl = (n) => {
    const t0 = Math.asin(halfW / 128);
    const out = [];
    for (let k = 0; k <= n; k++) {
      const t = -t0 + ((Math.PI + 2 * t0) * k) / n;
      out.push([50 + 128 * Math.sin(t), -92 - 103 * Math.cos(t)]);
    }
    return out;
  };

  /* 各ストロークの端は交差相手の面で止め、貫通による重なりを避ける */
  const strokes = [
    /* Tの横棒: 右端はRの縦棒の左面(x=30)まで */
    { hue: T_HUE, path: line([-190, -135], [30, -135], 9) },
    /* Tの縦棒: 上端は横棒の下面(y=-115)から */
    { hue: T_HUE, path: line([-60, -115], [-60, 185], 12) },
    { hue: R_HUE, path: line([50, -195], [50, 185], 14) } /* Rの縦棒 */,
    { hue: R_HUE, path: bowl(12) } /* Rのボウル */,
    /* Rの脚: ボウル下端の接合点(縦棒の右面)から生やす */
    { hue: R_HUE, path: quad([70, 10], [105, 120], [205, 185], 10) },
  ];

  strokes.forEach(({ hue, path }) => {
    const base = pts.length;
    path.forEach(([x, y], k) => {
      /* 進行方向の法線ベクトルで線幅方向にオフセット */
      const prev = path[Math.max(0, k - 1)];
      const next = path[Math.min(path.length - 1, k + 1)];
      let dx = next[0] - prev[0];
      let dy = next[1] - prev[1];
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = (-dy / len) * halfW;
      const ny = (dx / len) * halfW;
      /* 断面の四隅（角柱の4本のレール） */
      pts.push(
        [x + nx, y + ny, -depth],
        [x + nx, y + ny, depth],
        [x - nx, y - ny, depth],
        [x - nx, y - ny, -depth],
      );
      hues.push(hue, hue, hue, hue);
      const q = base + k * 4;
      /* 断面のリング */
      edges.push([q, q + 1], [q + 1, q + 2], [q + 2, q + 3], [q + 3, q]);
      /* 長手方向のレール */
      if (k > 0)
        for (let c = 0; c < 4; c++) edges.push([q - 4 + c, q + c]);
    });
  });

  return { pts, edges, hues };
}

const GEOMETRIES = {
  sphere: () => sphereGeo(225, 12, 20),
  mobius: () => mobiusGeo(250, 84, 44, 7),
  tesseract: () => tesseractGeo(100, 7),
  logo: () => logoGeo(),
};

const Canvas = styled.canvas`
  position: absolute;
  pointer-events: none;
  z-index: -1;
  opacity: 0.6;
  /* 内部解像度はDPR倍(width/height属性)のため、表示サイズは論理サイズで固定する。
     利用側(styled(OrbCanvas))のサイズ指定は注入順が後なのでこちらを上書きできる */
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;

  @media (max-width: 900px) {
    opacity: 0.45;
  }
`;

const OrbCanvas = ({
  type = "sphere",
  speed = 0.0075,
  tilt = 0.42,
  zoom = 1,
  size = 680,
  style,
  className,
}) => {
  const ref = useRef(null);

  useEffect(() => {
    const cv = ref.current;
    const g = cv.getContext("2d");
    const geo = GEOMETRIES[type]();
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches;
    /* Retinaでぼやけないよう内部解像度をDPRで上げ、描画はsize基準の論理座標のまま */
    const DPR = Math.min(window.devicePixelRatio || 1, 2);
    cv.width = size * DPR;
    cv.height = size * DPR;
    let rot = 0;
    let raf = 0;
    let alive = true;
    let needsDraw = true; /* 初回とイベント起因の再描画フラグ */

    /* 毎フレームのgetBoundingClientRect(強制レイアウト)を避けるためキャッシュ */
    let rect = cv.getBoundingClientRect();
    const refreshRect = () => {
      rect = cv.getBoundingClientRect();
      needsDraw = true;
    };
    window.addEventListener("resize", refreshRect, { passive: true });
    window.addEventListener("scroll", refreshRect, { passive: true });

    /* ---------- ドラッグ回転（マウス限定） ---------- */
    /* キャンバスはpointer-events:noneの装飾レイヤーなのでwindowで監視し、
       キャンバス領域内かつテキスト/リンク等の上でない場所からのドラッグだけ拾う */
    let yawOff = 0; /* ドラッグ由来のヨー回転（スクロール回転に加算） */
    let yawVel = 0; /* 慣性用の角速度 */
    let tiltOff = 0; /* ドラッグ由来のチルト */
    let dragging = false;
    let lx = 0;
    let ly = 0;
    let cursorSet = false;
    const INTERACTIVE =
      "a,button,input,textarea,select,label,p,h1,h2,h3,h4,ul,ol,table,img,span,em,strong";
    /* h5/dt/dd/div直下のテキストなどセレクタで拾えない文字の選択まで奪わないよう、
       要素が直接テキストを持つ場合もドラッグ対象外にする */
    const hasOwnText = (el) =>
      !!el.childNodes &&
      Array.from(el.childNodes).some(
        (n) => n.nodeType === 3 && n.textContent.trim(),
      );
    const canGrab = (e) =>
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom &&
      !(e.target.closest && e.target.closest(INTERACTIVE)) &&
      !hasOwnText(e.target);
    const onDown = (e) => {
      if (e.button !== 0 || e.pointerType !== "mouse" || !canGrab(e)) return;
      dragging = true;
      lx = e.clientX;
      ly = e.clientY;
      yawVel = 0;
      setGrabbing(true);
      document.body.style.userSelect = "none";
    };
    const onMove = (e) => {
      if (dragging) {
        const dx = e.clientX - lx;
        const dy = e.clientY - ly;
        lx = e.clientX;
        ly = e.clientY;
        /* 前面(手前側)がドラッグ方向に付いてくる向きに回す */
        yawOff -= dx * 0.008;
        yawVel = -dx * 0.008;
        tiltOff = Math.max(-1.1, Math.min(1.1, tiltOff + dy * 0.005));
        needsDraw = true;
        if (reduce) schedule();
      } else if (e.pointerType === "mouse") {
        /* つかめる場所ではカーソルで示す */
        const grab = canGrab(e);
        if (grab !== cursorSet) {
          setGrab(grab);
          cursorSet = grab;
        }
      }
    };
    const onUp = () => {
      if (!dragging) return;
      dragging = false;
      if (reduce) yawVel = 0; /* reduced-motion時は慣性で回さない */
      setGrabbing(false);
      document.body.style.userSelect = "";
    };
    window.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerup", onUp);

    /* 静止判定: trueを返すとそのフレームの再描画を丸ごとスキップする。
       rotTarget: このフレームの回転目標値(スクロール量+ドラッグ由来のヨー) */
    const isSettled = (rotTarget) =>
      /* 0.0005rad ≒ 半径250pxで約0.1pxの変化。これ未満なら見た目が動かない */
      Math.abs(rotTarget - rot) < 0.0005 && Math.abs(yawVel) < 0.0005;

    const draw = () => {
      if (!alive) return;
      if (!reduce) raf = requestAnimationFrame(draw);
      if (rect.bottom < 0 || rect.top > window.innerHeight) return; // 画面外はスキップ
      if (!dragging && !reduce) {
        yawOff += yawVel; /* 離した後は慣性で回り続けて減衰 */
        yawVel *= 0.95;
      }
      const rotTarget = window.scrollY * speed + yawOff;
      /* 静止中(スクロール・ドラッグ・慣性がすべて収束)はフレームを丸ごとスキップし、
         fixed配置でも無駄なフル再描画が走り続けないようにする */
      if (!needsDraw && isSettled(rotTarget)) return;
      needsDraw = false;
      g.setTransform(DPR, 0, 0, DPR, 0, 0);
      g.clearRect(0, 0, size, size);
      /* reduced-motion時はlerpの補間アニメを省き、目標値へ直接スナップ */
      if (reduce) rot = rotTarget;
      else rot += (rotTarget - rot) * 0.09;
      /* 4次元ジオメトリはまずXW平面で回転させ、4D→3Dへ透視投影 */
      let src = geo.pts;
      if (geo.dim4) {
        const a4 = rot * 0.9;
        const c4 = Math.cos(a4);
        const s4 = Math.sin(a4);
        const S4 = geo.S4;
        src = src.map((p) => {
          const x = p[0] * c4 - p[3] * s4;
          const w = p[0] * s4 + p[3] * c4;
          const k = 2.4 / (2.4 - w / S4); /* wが手前ほど大きく */
          return [x * k, p[1] * k, p[2] * k];
        });
      }
      const cy = Math.cos(rot);
      const sy = Math.sin(rot);
      const tiltNow = tilt + tiltOff;
      const cxt = Math.cos(tiltNow);
      const sxt = Math.sin(tiltNow);
      const W = size;
      const H = size;
      const f = 560;
      const proj = src.map((p, i) => {
        const x = p[0] * cy + p[2] * sy;
        const z = -p[0] * sy + p[2] * cy;
        const y = p[1];
        const y2 = y * cxt - z * sxt;
        const z2 = y * sxt + z * cxt;
        const s = (f / (f + z2 + 300)) * zoom;
        /* 固定色指定があれば使用、なければ3D位置から色相を算出（回すと虹が巡る） */
        const h = geo.hues
          ? geo.hues[i]
          : (((Math.atan2(p[2], p[0]) / Math.PI) * 180 +
              180 +
              p[1] * 0.35 +
              rot * 40) %
              360 +
              360) %
            360;
        return { x: W / 2 + x * s, y: H / 2 + y2 * s, z: z2, s, h };
      });
      /* 面の塗りつぶし（奥→手前の順、面ごとにパステルカラー） */
      if (geo.faces) {
        const PASTEL = [
          "352,100,86",
          "28,100,86",
          "58,100,86",
          "135,100,86",
          "208,100,86",
          "268,100,86",
        ];
        const fs = geo.faces.map((face, i) => ({
          face,
          i,
          z: face.reduce((s, idx) => s + proj[idx].z, 0) / face.length,
        }));
        fs.sort((a, b) => b.z - a.z);
        fs.forEach(({ face, i, z }) => {
          const al =
            Math.min(0.5, Math.max(0.08, 0.3 - z / 620)) / (geo.dim4 ? 2.2 : 1);
          const [hh, ss, ll] = PASTEL[i % PASTEL.length].split(",");
          g.fillStyle = `hsla(${hh},${ss}%,${ll}%,${al})`;
          g.beginPath();
          face.forEach((idx, k) =>
            k
              ? g.lineTo(proj[idx].x, proj[idx].y)
              : g.moveTo(proj[idx].x, proj[idx].y),
          );
          g.closePath();
          g.fill();
        });
      }
      /* 固定色ジオメトリ（ロゴ等）はブランドカラーが映えるよう少し濃いめに */
      const edgeL = geo.hues ? 58 : 78;
      const edgeA = geo.hues ? 0.5 : 0.32;
      g.lineWidth = geo.hues ? 1.5 : 1;
      geo.edges.forEach(([a, b]) => {
        const p = proj[a];
        const q = proj[b];
        const al = Math.max(0.05, edgeA - (p.z + q.z) / 2200);
        g.strokeStyle = `hsla(${p.h},70%,${edgeL}%,${al})`;
        g.beginPath();
        g.moveTo(p.x, p.y);
        g.lineTo(q.x, q.y);
        g.stroke();
      });
      proj.forEach((p) => {
        g.beginPath();
        g.arc(p.x, p.y, Math.max(0.7, 2.2 * p.s), 0, Math.PI * 2);
        g.fillStyle = `hsla(${p.h},85%,${
          geo.hues ? (p.z < 0 ? 68 : 58) : p.z < 0 ? 84 : 74
        }%,${Math.min(0.95, Math.max(0.15, 0.85 - p.z / 320))})`;
        g.fill();
      });
    };

    /* reduced-motion時の単発描画。同一フレームに複数イベントが来ても1回に集約 */
    let pending = false;
    const schedule = () => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        draw();
      });
    };
    const onScroll = () => schedule();
    if (reduce) window.addEventListener("scroll", onScroll, { passive: true });

    draw();
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", refreshRect);
      window.removeEventListener("scroll", refreshRect);
      window.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      if (dragging) setGrabbing(false);
      if (cursorSet) setGrab(false);
      document.body.style.userSelect = "";
      if (reduce) window.removeEventListener("scroll", onScroll);
    };
  }, [type, speed, tilt, zoom, size]);

  return (
    <Canvas
      ref={ref}
      $size={size}
      width={size}
      height={size}
      style={style}
      className={className}
      aria-hidden="true"
    />
  );
};

export default OrbCanvas;
